import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, useToast, Input } from '@chakra-ui/react';
import { HttpStatusCode } from 'axios';

import { COLORS, DURATION_TOAST } from '@/common/constants';
import TableGroupedShipOwner from '@/components/Tables/TableGroupedShipOwner';
import { getGroupedShipOwners } from '@/services/shipOwnerService';

import '../styles/ListPage.css';

// Define a type for the ship owner object for better type safety
interface ShipOwner {
    onBehalfCompany: string;
    // ... other properties you might need
    address: string;
    country: string;
    contactPerson: string;
    email: string;
    alternateEmail: string | null;
    phone: string;
    existNewVessel: boolean;
    documents: string;
    status: number;
    note: string | null;
    vessels: any[] | null;
    invoices: any[] | null;
    vesselIMONumbers: string;
    created: string;
    createdBy: string;
    isDeleted: boolean;
    id: string;
    modified: string;
    modifiedBy: string;
}

const GroupedShipOwnerList = () => {
    const navigate = useNavigate();
    const toast = useToast();

    // Change the type of dataList to ShipOwner[]
    const [dataList, setDataList] = useState<ShipOwner[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const showToast = (title: string, description: string, status: 'success' | 'error') => {
        toast({
            title,
            description,
            status,
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const getData = async () => {
        setIsLoading(true);

        try {
            const response = await getGroupedShipOwners();
            if (response.status === HttpStatusCode.Ok) {
                console.log('Grouped Ship Owners Response:', response.groups);

                if (response?.groups && response.groups.length > 0) {
                    // response.groups is an array of arrays (groups of ShipOwners)
                    const groups: ShipOwner[][] = response.groups;

                    // Select the ShipOwner with the shortest onBehalfCompany name from each group
                    const parents: ShipOwner[] = groups.map((group) =>
                        group.reduce((a, b) =>
                            a.onBehalfCompany.length <= b.onBehalfCompany.length ? a : b
                        )
                    );

                    console.log('Ship Owners with Shortest Company Names in Each Group:', JSON.stringify(parents));
                    setDataList(parents);
                } else {
                    setDataList([]);
                }
            } else {
                // Ensure error structure is handled safely
                const errorMessage = response.error?.message || 'An unknown error occurred.';
                const errorStatus = response.error?.status || 'error';
                showToast('Error', errorMessage, errorStatus);
            }
        } catch (e) {
            console.error(e);
            showToast('Error', 'Failed to fetch ship operators.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const [searchValue, setSearchValue] = useState('');

    useEffect(() => {
        getData();
    }, []);

    // --- Search Implementation Start ---
    const filteredDataList = useMemo(() => {
        if (!searchValue) {
            return dataList;
        }

        const lowerCaseSearchValue = searchValue.toLowerCase();

        return dataList.filter((item) =>
            item.onBehalfCompany.toLowerCase().includes(lowerCaseSearchValue)
        );
    }, [dataList, searchValue]);

    return (
        <>
            <div className="table-container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        <div className="table-filter-input" style={{ width: '300px' }}>
                           <Input
                                placeholder="Search by Company Name"
                                value={searchValue}
                                onChange={e => setSearchValue(e.target.value)}
                            />
                        </div>
                        <div className="table-filter-separator" />
                        <Button
                            bgColor={COLORS.BUTTON.BACKGROUND}
                            color={COLORS.BUTTON.FOREGROUND}
                            borderRadius={10}
                            onClick={() => navigate('/manage-ship-operator')}
                        >
                            All List
                        </Button>
                    </div>
                </div>
                {/* Use filteredDataList instead of dataList */}
                <TableGroupedShipOwner
                    dataList={filteredDataList}
                    isLoading={isLoading}
                />
            </div>
        </>
    );
};

export default GroupedShipOwnerList;